// frontend/src/contexts/CRMContext.js
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { SubscriptionProvider } from './SubscriptionContext';

const CRMContext = createContext();

export const useCRM = () => {
    const context = useContext(CRMContext);
    if (!context) {
        throw new Error('useCRM must be used within CRMProvider');
    }
    return context;
};

export const CRMProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [currentAgency, setCurrentAgency] = useState(null);
    const [agencies, setAgencies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [token, setToken] = useState(localStorage.getItem('crm_token'));
    const [backendError, setBackendError] = useState(null);
    const agencyLoadedRef = useRef(false);

    // Configure axios defaults
    useEffect(() => {
        if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            localStorage.setItem('crm_token', token);
        } else {
            delete axios.defaults.headers.common['Authorization'];
            localStorage.removeItem('crm_token');
        }
    }, [token]);

    const logout = useCallback(() => {
        setToken(null);
        setUser(null);
        setCurrentAgency(null);
        setAgencies([]);
        agencyLoadedRef.current = false;
        localStorage.removeItem('crm_token');
        localStorage.removeItem('current_agency_id');
        // Clear axios headers
        delete axios.defaults.headers.common['Authorization'];
        delete axios.defaults.headers.common['X-Agency-Id'];
    }, []);

    // Load current user and agencies
    useEffect(() => {
        const loadUser = async () => {
            if (!token) {
                setLoading(false);
                return;
            }

            try {
                // Normalize API URL (remove trailing slash to avoid double slashes)
                const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/+$/, '');
                const [userResponse, agenciesResponse] = await Promise.all([
                    axios.get(`${apiUrl}/api/crm/auth/me`),
                    axios.get(`${apiUrl}/api/crm/agencies`)
                ]);

                if (userResponse.data.success) {
                    setUser(userResponse.data.user);
                }

                if (agenciesResponse.data.success) {
                    setAgencies(agenciesResponse.data.data);
                    
                    // Always try to set current agency if not set, or if user has currentAgencyId that differs
                    const userCurrentAgencyId = userResponse.data.user.currentAgencyId;
                    const storedAgencyId = localStorage.getItem('current_agency_id');
                    const currentAgencyId = currentAgency?._id || currentAgency?.id;
                    
                    // Determine which agency ID to use (priority: stored > user.currentAgencyId > current)
                    let targetAgencyId = storedAgencyId || userCurrentAgencyId || currentAgencyId;
                    
                    // If we don't have a current agency or it doesn't match the target, set it
                    if (!currentAgency || (targetAgencyId && String(currentAgencyId) !== String(targetAgencyId))) {
                        let agencyToSet = null;
                        
                        if (targetAgencyId) {
                            // Try to find the target agency
                            agencyToSet = agenciesResponse.data.data.find(
                                a => String(a._id) === String(targetAgencyId) || String(a.id) === String(targetAgencyId)
                            );
                        }
                        
                        // If still not found and user has currentAgencyId, try that
                        if (!agencyToSet && userCurrentAgencyId) {
                            agencyToSet = agenciesResponse.data.data.find(
                                a => String(a._id) === String(userCurrentAgencyId) || String(a.id) === String(userCurrentAgencyId)
                            );
                        }
                        
                        // Final fallback: use first agency
                        if (!agencyToSet && agenciesResponse.data.data.length > 0) {
                            agencyToSet = agenciesResponse.data.data[0];
                        }
                        
                        if (agencyToSet) {
                            const agencyId = agencyToSet._id || agencyToSet.id;
                            setCurrentAgency(agencyToSet);
                            localStorage.setItem('current_agency_id', String(agencyId));
                            // Set axios header for agency context
                            axios.defaults.headers.common['X-Agency-Id'] = String(agencyId);
                            agencyLoadedRef.current = true;
                            console.log('✅ Set current agency:', agencyId, agencyToSet.name);
                        }
                    }
                }
            } catch (error) {
                console.error('Failed to load user:', error);
                
                // Check if backend is unreachable (network error)
                if (!error.response) {
                    // Network error - backend is unreachable
                    console.warn('⚠️ Network error on user load, backend unreachable');
                    setBackendError('Unable to connect to the server. Please check your internet connection and try again.');
                } else {
                    // Backend responded, so it's reachable - clear any previous backend error
                    setBackendError(null);
                    
                    // Only clear token if it's a definitive authentication error
                    // Network errors or temporary backend issues shouldn't clear the token
                    if (error.response?.status === 401) {
                        const errorMessage = error.response?.data?.message || '';
                        // Only clear if it's explicitly an auth error, not a generic 401
                        if (errorMessage.includes('token') || 
                            errorMessage.includes('authentication') || 
                            errorMessage.includes('unauthorized') ||
                            errorMessage.includes('invalid') ||
                            errorMessage.includes('expired')) {
                            console.warn('⚠️ Authentication failed, clearing token:', errorMessage);
                            setToken(null);
                            setUser(null);
                            setCurrentAgency(null);
                            localStorage.removeItem('crm_token');
                            localStorage.removeItem('current_agency_id');
                        } else {
                            // Temporary error, keep token and retry later
                            console.warn('⚠️ Temporary error on user load, keeping token:', errorMessage);
                        }
                    }
                }
            } finally {
                setLoading(false);
            }
        };

        loadUser();
    }, [token, logout]);
    
    // Watch for changes in user.currentAgencyId and set currentAgency if needed
    useEffect(() => {
        if (user?.currentAgencyId && agencies.length > 0) {
            const currentAgencyId = currentAgency?._id || currentAgency?.id;
            const userAgencyId = user.currentAgencyId;
            
            // If currentAgency doesn't match user's currentAgencyId, update it
            if (String(currentAgencyId) !== String(userAgencyId)) {
                const agencyToSet = agencies.find(
                    a => String(a._id) === String(userAgencyId) || String(a.id) === String(userAgencyId)
                );
                
                if (agencyToSet) {
                    console.log('✅ Updating current agency from user.currentAgencyId:', userAgencyId);
                    setCurrentAgency(agencyToSet);
                    localStorage.setItem('current_agency_id', String(userAgencyId));
                    axios.defaults.headers.common['X-Agency-Id'] = String(userAgencyId);
                }
            }
        } else if (user?.currentAgencyId && !currentAgency && agencies.length > 0) {
            // User has currentAgencyId but we don't have currentAgency set yet
            const userAgencyId = user.currentAgencyId;
            const agencyToSet = agencies.find(
                a => String(a._id) === String(userAgencyId) || String(a.id) === String(userAgencyId)
            );
            
            if (agencyToSet) {
                console.log('✅ Setting current agency from user.currentAgencyId:', userAgencyId);
                setCurrentAgency(agencyToSet);
                localStorage.setItem('current_agency_id', String(userAgencyId));
                axios.defaults.headers.common['X-Agency-Id'] = String(userAgencyId);
            }
        }
    }, [user?.currentAgencyId, agencies, currentAgency]);


    const login = useCallback(async (newToken, userData) => {
        console.log('🔐 CRMContext login called with user:', userData?.email);
        setToken(newToken);
        setUser(userData);
        
        // Immediately load agencies after login
        try {
            // Normalize API URL (remove trailing slash to avoid double slashes)
            const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/+$/, '');
            console.log('🔐 Fetching agencies...');
            const response = await axios.get(`${apiUrl}/api/crm/agencies`, {
                headers: {
                    'Authorization': `Bearer ${newToken}`
                }
            });
            
            console.log('🔐 Agencies response:', response.data);
            
            if (response.data.success && response.data.data.length > 0) {
                console.log('🔐 Setting agencies:', response.data.data.length);
                setAgencies(response.data.data);
                
                // Set current agency
                const storedAgencyId = localStorage.getItem('current_agency_id');
                let agencyToSet = null;
                
                if (storedAgencyId) {
                    agencyToSet = response.data.data.find(a => a._id === storedAgencyId);
                    console.log('🔐 Found stored agency:', agencyToSet?.name);
                }
                
                if (!agencyToSet && userData.currentAgencyId) {
                    agencyToSet = response.data.data.find(a => a._id === userData.currentAgencyId);
                    console.log('🔐 Using user currentAgencyId:', agencyToSet?.name);
                }
                
                if (!agencyToSet) {
                    agencyToSet = response.data.data[0];
                    console.log('🔐 Using first agency:', agencyToSet?.name);
                }
                
                if (agencyToSet) {
                    console.log('🔐 Setting current agency:', agencyToSet.name, agencyToSet._id);
                    setCurrentAgency(agencyToSet);
                    localStorage.setItem('current_agency_id', agencyToSet._id);
                    axios.defaults.headers.common['X-Agency-Id'] = agencyToSet._id;
                    agencyLoadedRef.current = true;
                    console.log('✅ Login successful - Agency set:', agencyToSet.name);
                } else {
                    console.warn('⚠️ No agency to set');
                }
            } else {
                console.warn('⚠️ No agencies returned or empty array');
            }
        } catch (error) {
            console.error('❌ Failed to load agencies after login:', error);
        }
    }, []);

    const switchAgency = useCallback(async (agencyId) => {
        try {
            // Normalize API URL (remove trailing slash to avoid double slashes)
            const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/+$/, '');
            const agencyIdStr = String(agencyId);
            
            console.log('🔄 switchAgency called with:', agencyIdStr);
            
            const response = await axios.post(`${apiUrl}/api/crm/auth/switch-agency`, {
                agencyId: agencyIdStr
            });

            if (response.data.success) {
                // Update axios header for agency context immediately
                axios.defaults.headers.common['X-Agency-Id'] = agencyIdStr;
                localStorage.setItem('current_agency_id', agencyIdStr);

                // Refresh user data to get updated currentAgencyId
                const userResponse = await axios.get(`${apiUrl}/api/crm/auth/me`);
                if (userResponse.data.success) {
                    setUser(userResponse.data.user);
                    console.log('✅ User data refreshed, currentAgencyId:', userResponse.data.user.currentAgencyId);
                }

                // Fetch the full agency details
                const agencyResponse = await axios.get(`${apiUrl}/api/crm/agencies/${agencyIdStr}`);
                if (agencyResponse.data.success) {
                    const newCurrent = agencyResponse.data.data;
                    console.log('✅ Setting current agency to:', newCurrent.name, newCurrent._id);
                    setCurrentAgency(newCurrent);
                    
                    // Update the agency in the agencies list
                    setAgencies(prev => {
                        const updated = prev.map(agency => 
                            String(agency._id) === String(agencyIdStr) ? newCurrent : agency
                        );
                        // If agency not in list, add it
                        if (!updated.find(a => String(a._id) === String(agencyIdStr))) {
                            updated.push(newCurrent);
                        }
                        return updated;
                    });
                } else {
                    // Fallback: find in existing list
                    const newCurrent = agencies.find(a => String(a._id) === String(agencyIdStr));
                    if (newCurrent) {
                        console.log('✅ Using agency from existing list:', newCurrent.name);
                        setCurrentAgency(newCurrent);
                    } else {
                        // Last resort: reload agencies list
                        const agenciesResponse = await axios.get(`${apiUrl}/api/crm/agencies`);
                        if (agenciesResponse.data.success) {
                            setAgencies(agenciesResponse.data.data);
                            const found = agenciesResponse.data.data.find(a => String(a._id) === String(agencyIdStr));
                            if (found) {
                                console.log('✅ Found agency after reload:', found.name);
                                setCurrentAgency(found);
                            }
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Failed to switch agency:', error);
            throw error;
        }
    }, [agencies]);

    const refreshCurrentAgency = useCallback(async () => {
        if (!currentAgency?._id) return;
        
        try {
            // Normalize API URL (remove trailing slash to avoid double slashes)
            const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/+$/, '');
            const response = await axios.get(`${apiUrl}/api/crm/agencies/${currentAgency._id}`);
            
            if (response.data.success) {
                setCurrentAgency(response.data.data);
                
                // Also update in agencies array
                setAgencies(prev => prev.map(agency => 
                    agency._id === currentAgency._id ? response.data.data : agency
                ));
            }
        } catch (error) {
            console.error('Failed to refresh agency:', error);
        }
    }, [currentAgency?._id]);

    const forceRefresh = useCallback(() => {
        setLoading(true);
        // Clear current state
        setUser(null);
        setCurrentAgency(null);
        setAgencies([]);
        agencyLoadedRef.current = false;
        
        // Reload user and agencies
        const loadUser = async () => {
            if (!token) {
                setLoading(false);
                return;
            }
            try {
                // Normalize API URL (remove trailing slash to avoid double slashes)
                const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/+$/, '');
                const [userResponse, agenciesResponse] = await Promise.all([
                    axios.get(`${apiUrl}/api/crm/auth/me`),
                    axios.get(`${apiUrl}/api/crm/agencies`)
                ]);
                // Backend is reachable - clear any backend error
                setBackendError(null);
                
                if (userResponse.data.success) {
                    setUser(userResponse.data.user);
                }
                if (agenciesResponse.data.success) {
                    setAgencies(agenciesResponse.data.data);
                    if (agenciesResponse.data.data.length > 0) {
                        setCurrentAgency(agenciesResponse.data.data[0]);
                        localStorage.setItem('current_agency_id', agenciesResponse.data.data[0]._id);
                        axios.defaults.headers.common['X-Agency-Id'] = agenciesResponse.data.data[0]._id;
                    }
                }
            } catch (error) {
                console.error('Force refresh failed:', error);
                
                // Check if backend is unreachable (network error)
                if (!error.response) {
                    // Network error - backend is unreachable
                    console.warn('⚠️ Network error on force refresh, backend unreachable');
                    setBackendError('Unable to connect to the server. Please check your internet connection and try again.');
                } else {
                    // Backend responded, so it's reachable - clear any previous backend error
                    setBackendError(null);
                    
                    // Only logout on authentication errors
                    if (error.response?.status === 401) {
                        const errorMessage = error.response?.data?.message || '';
                        if (errorMessage.includes('token') || 
                            errorMessage.includes('authentication') || 
                            errorMessage.includes('unauthorized') ||
                            errorMessage.includes('invalid') ||
                            errorMessage.includes('expired')) {
                            logout();
                        }
                    }
                }
            } finally {
                setLoading(false);
            }
        };
        loadUser();
    }, [token, logout]);

    // API call helper function
    const apiCall = useCallback(async (endpoint, options = {}) => {
        // Normalize API URL (remove trailing slash to avoid double slashes)
        const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/+$/, '');
        const url = endpoint.startsWith('http') ? endpoint : `${apiUrl}${endpoint}`;
        
        const config = {
            headers: {
                'Content-Type': 'application/json',
                ...(token && { Authorization: `Bearer ${token}` }),
                ...(currentAgency?._id && { 'X-Agency-Id': currentAgency._id }),
                ...options.headers
            },
            ...options
        };

        try {
            const response = await axios(url, config);
            return response.data;
        } catch (error) {
            console.error('API call failed:', error);
            throw error;
        }
    }, [token, currentAgency?._id]);

    // Agency management functions
    const createAgency = useCallback(async (agencyData) => {
        try {
            // Normalize API URL (remove trailing slash to avoid double slashes)
            const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/+$/, '');
            const response = await axios.post(`${apiUrl}/api/crm/agencies`, agencyData);
            
            if (response.data.success) {
                const newAgency = response.data.data;
                setAgencies(prev => [...prev, newAgency]);
                
                // Refresh user data to get updated agencyMemberships
                // Use retry logic to handle potential race conditions
                let retryCount = 0;
                const maxRetries = 3;
                
                while (retryCount < maxRetries) {
                    try {
                        // Add small delay to ensure backend has saved
                        if (retryCount > 0) {
                            await new Promise(resolve => setTimeout(resolve, 500 * retryCount));
                        }
                        
                        // Normalize API URL (remove trailing slash to avoid double slashes)
                        const normalizedApiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/+$/, '');
                        const [userResponse, agenciesResponse] = await Promise.all([
                            axios.get(`${normalizedApiUrl}/api/crm/auth/me`),
                            axios.get(`${normalizedApiUrl}/api/crm/agencies`)
                        ]);
                        
                        if (userResponse.data.success) {
                            const updatedUser = userResponse.data.user;
                            console.log('✅ User refreshed after agency creation (attempt ' + (retryCount + 1) + '):', {
                                userId: updatedUser._id,
                                memberships: updatedUser.agencyMemberships?.length || 0,
                                currentAgencyId: updatedUser.currentAgencyId,
                                memberships: updatedUser.agencyMemberships?.map(m => ({
                                    agencyId: m.agencyId?.toString() || m.agencyId,
                                    role: m.role,
                                    status: m.status
                                }))
                            });
                            
                            // Check if memberships were actually updated
                            if (updatedUser.agencyMemberships && updatedUser.agencyMemberships.length > 0) {
                                setUser(updatedUser);
                                
                                if (agenciesResponse.data.success) {
                                    const agenciesList = agenciesResponse.data.data || [];
                                    setAgencies(agenciesList);
                                    
                                    // Find and set the newly created agency as current
                                    const agencyId = newAgency._id || newAgency.id || updatedUser.currentAgencyId;
                                    if (agencyId) {
                                        const foundAgency = agenciesList.find(
                                            a => String(a._id) === String(agencyId) || String(a.id) === String(agencyId)
                                        );
                                        if (foundAgency) {
                                            console.log('✅ Setting new agency as current:', agencyId);
                                            setCurrentAgency(foundAgency);
                                            localStorage.setItem('current_agency_id', String(agencyId));
                                            axios.defaults.headers.common['X-Agency-Id'] = String(agencyId);
                                            agencyLoadedRef.current = false; // Reset flag to allow re-loading
                                            break; // Success, exit retry loop
                                        } else {
                                            console.warn('⚠️ Newly created agency not found in agencies list');
                                        }
                                    }
                                }
                                
                                break; // Success, exit retry loop
                            } else if (retryCount < maxRetries - 1) {
                                console.log('⚠️ User memberships not yet updated, retrying...');
                                retryCount++;
                            } else {
                                console.warn('⚠️ User memberships still not updated after retries');
                                setUser(updatedUser); // Set user anyway
                                break;
                            }
                        } else {
                            break; // Response received but not successful
                        }
                    } catch (refreshError) {
                        console.error(`Failed to refresh user data after agency creation (attempt ${retryCount + 1}):`, refreshError);
                        if (retryCount < maxRetries - 1) {
                            retryCount++;
                            continue; // Retry
                        } else {
                            // Final attempt failed, but don't fail the whole operation
                            console.error('⚠️ Failed to refresh user data after all retries');
                            break;
                        }
                    }
                }
                
                return { success: true, data: newAgency };
            }
            return { success: false, error: response.data.message };
        } catch (error) {
            console.error('Failed to create agency:', error);
            return { success: false, error: error.response?.data?.message || 'Failed to create agency' };
        }
    }, []);

    const updateAgency = useCallback(async (agencyId, agencyData) => {
        try {
            // Normalize API URL (remove trailing slash to avoid double slashes)
            const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/+$/, '');
            const response = await axios.put(`${apiUrl}/api/crm/agencies/${agencyId}`, agencyData);
            
            if (response.data.success) {
                const updatedAgency = response.data.data;
                setAgencies(prev => prev.map(agency => 
                    agency._id === agencyId ? updatedAgency : agency
                ));
                
                // Update current agency if it's the one being updated
                if (currentAgency?._id === agencyId) {
                    setCurrentAgency(updatedAgency);
                }
                
                return { success: true, data: updatedAgency };
            }
            return { success: false, error: response.data.message };
        } catch (error) {
            console.error('Failed to update agency:', error);
            return { success: false, error: error.response?.data?.message || 'Failed to update agency' };
        }
    }, [currentAgency?._id]);

    const deleteAgency = useCallback(async (agencyId) => {
        try {
            // Normalize API URL (remove trailing slash to avoid double slashes)
            const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/+$/, '');
            const response = await axios.delete(`${apiUrl}/api/crm/agencies/${agencyId}`);
            
            if (response.data.success) {
                setAgencies(prev => prev.filter(agency => agency._id !== agencyId));
                
                // If we deleted the current agency, switch to another one
                if (currentAgency?._id === agencyId) {
                    const remainingAgencies = agencies.filter(agency => agency._id !== agencyId);
                    if (remainingAgencies.length > 0) {
                        await switchAgency(remainingAgencies[0]._id);
                    } else {
                        setCurrentAgency(null);
                        localStorage.removeItem('current_agency_id');
                    }
                }
                
                return { success: true };
            }
            return { success: false, error: response.data.message };
        } catch (error) {
            console.error('Failed to delete agency:', error);
            return { success: false, error: error.response?.data?.message || 'Failed to delete agency' };
        }
    }, [currentAgency?._id, agencies, switchAgency]);

    // Check if user can create more agencies based on their tier
    const canCreateAgency = useCallback(() => {
        if (!user) return false;
        const userId = user._id || user.id;
        const owned = agencies.filter(a => {
            const ownerId = a.ownerUserId?._id || a.ownerUserId || a.createdBy?._id || a.createdBy;
            return String(ownerId) === String(userId);
        });
        // Allow at least one
        if (owned.length === 0) return true;
        const hasEnterpriseOwned = owned.some(a => {
            const plan = a.planTier || a.subscription?.plan;
            const status = a.status || a.subscription?.status;
            return plan === 'enterprise' && ['active','grace'].includes(status);
        });
        return hasEnterpriseOwned; // unlimited when true; otherwise already owns 1
    }, [user, agencies]);

    // Get remaining agency slots
    const getRemainingAgencySlots = useCallback(() => {
        if (!user) return 0;
        const userId = user._id || user.id;
        const owned = agencies.filter(a => {
            const ownerId = a.ownerUserId?._id || a.ownerUserId || a.createdBy?._id || a.createdBy;
            return String(ownerId) === String(userId);
        });
        if (owned.length === 0) return 1;
        const hasEnterpriseOwned = owned.some(a => {
            const plan = a.planTier || a.subscription?.plan;
            const status = a.status || a.subscription?.status;
            return plan === 'enterprise' && ['active','grace'].includes(status);
        });
        return hasEnterpriseOwned ? 'unlimited' : 0;
    }, [user, agencies]);

    const value = useMemo(() => ({
        user,
        currentAgency,
        agencies,
        loading,
        token,
        backendError,
        login,
        logout,
        switchAgency,
        refreshCurrentAgency,
        setCurrentAgency,
        forceRefresh,
        apiCall,
        createAgency,
        updateAgency,
        deleteAgency,
        canCreateAgency,
        getRemainingAgencySlots,
        isAuthenticated: !!user,
        isSuperadmin: user?.isSuperadmin || false
    }), [user, currentAgency, agencies, loading, token, backendError, login, logout, switchAgency, refreshCurrentAgency, setCurrentAgency, forceRefresh, apiCall, createAgency, updateAgency, deleteAgency, canCreateAgency, getRemainingAgencySlots]);

    return (
        <CRMContext.Provider value={value}>
            <SubscriptionProvider>
                {children}
            </SubscriptionProvider>
        </CRMContext.Provider>
    );
};

export default CRMContext;

